import { color, font, query, space } from '@design/templateFns';
import { ThemeFontName } from '@design/types';
import { Error } from '@icon/Error';
import { FC } from 'react';
import styled from 'styled-components';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import es from 'react-phone-input-2/lang/es.json';
import fr from 'react-phone-input-2/lang/fr.json';

export const FormWrapper = styled.form`
  display: flex;
  max-width: 500px;
  flex-direction: column;
`;

export const FormLabel = styled.label<{ $bold?: boolean; $isContactForm?: boolean }>`
  ${({ $isContactForm }) => ($isContactForm ? font('body-sm') : font('body-md'))};
  color: ${color('fg')};
  width: 100%;
  padding-bottom: 2px;
  display: inline-flex;
  line-height: ${({ $isContactForm }) => ($isContactForm ? '24px' : '30px')};
  letter-spacing: ${({ $isContactForm }) => $isContactForm && '0.42px'};

  @media screen and (${query.below('tablet')}) {
    ${({ $isContactForm }) => !$isContactForm && font('body-sm')}
  }

  // bold
  ${({ $bold }) =>
    $bold &&
    `
    font-weight: bold;
  `}
`;

export const FormField = styled.div`
  width: 100%;
  padding-bottom: 20px;
  display: inline-flex;
  flex-direction: column;
`;

export const InputField = styled.input<{
  $font?: ThemeFontName;
  $icon?: string;
  $iconX?: string;
  $iconY?: string;
  $isError?: boolean;
}>`
  width: 20px;
  ${({ $font }) => ($font ? font($font) : font('body-sm-light'))};
  color: ${color('gray4')};
  display: inline-flex;
  outline: none;
  border-radius: 4px;
  border: 1px solid ${color('gray2')};
  width: auto;
  height: 40px;
  padding: 0 16px;

  @media screen and (${query.atLeast('desktop')}) {
    ${font('body-md-light')};
  }

  &::placeholder {
    color: ${color('gray4')};
  }
  &:focus {
    color: ${color('fg')};
    border: 1px solid ${color('fg')};
  }
  &.warning {
    border: 1px solid ${color('warning')};
  }
  // icon
  ${({ $icon, $iconX, $iconY }) =>
    $icon &&
    `
    appearance: none;
    background-image: url(${$icon});
    background-size: 22px;
    background-repeat: no-repeat;
    background-position: ${$iconX || '97%'} ${$iconY || '12px'};
  `}
`;

// Input group is a an input plus a square button on the right
export const InputGroup = styled.div`
  display: flex;

  form {
    // Incase there is an inner form
    display: flex;
    width: 100%;
  }

  input {
    position: relative;
    flex: 1 1 auto;
    width: 1%;
    min-width: 0;
    ${font('body-sm-light')};
    color: ${color('gray4')};
    font-size: 16px;
    background-color: ${color('bg')};
    padding: ${space('sm')} ${space('md')};
    border: 1px solid ${color('gray2')};
    appearance: none;
    border-radius: 4px 0 0 4px;
    box-sizing: border-box;
    transition: background-color 0.5s ease-in-out;

    &:focus {
      outline: none;
      background-color: rgba(255, 255, 255, 1);
    }

    &::placeholder {
      color: ${color('gray4')};
    }

    &:focus::placeholder {
      color: transparent;
    }

    &::-webkit-search-cancel-button {
      -webkit-appearance: none;
      height: ${space('lg')};
      width: ${space('lg')};
      background-image: url("data:image/svg+xml,%3Csvg width='16px' height='16px' color='currentColor' viewBox='0 0 22 22' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M17,5L5,17' stroke='currentColor' stroke-width='1.5'%3E%3C/path%3E%3Cpath d='M17,17L5,5' stroke='currentColor' stroke-width='1.5'%3E%3C/path%3E%3C/svg%3E");
      background-size: contain;
      opacity: 1;
      cursor: pointer;
    }

    @media screen and (${query.atLeast('tablet')}) {
      ${font('body-sm-light')}

      &:focus {
        box-shadow: 0 0 0 3px hsl(220deg 100% 50% / 80%);
      }
    }
  }

  button {
    background-color: ${color('cta')};
    cursor: pointer;
    height: inherit;
    border: none;
    border-radius: 0 4px 4px 0;
    padding: ${space('sm')};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s ease-in-out;

    @media screen and (${query.atLeast('tablet')}) {
      padding: ${space('sm')} ${space('md')};
    }

    svg {
      width: ${space('xl')};
      height: ${space('xl')};
    }

    &:hover {
      background-color: ${color('warning')};
    }
  }
`;

export const TextArea = styled.textarea`
  ${font('body-md')};
  color: ${color('gray4')};
  display: inline-flex;
  outline: none;
  border-radius: 2px;
  border: 1px solid ${color('gray2')};
  width: auto;
  height: auto;
  padding: 10px;
  resize: none;

  &::placeholder {
    color: ${color('gray4')};
  }

  &:focus {
    color: ${color('fg')};
    border: 1px solid ${color('fg')};
  }
`;

export const SelectInput = styled.select<{ $font?: ThemeFontName }>`
  ${({ $font }) => ($font ? font($font) : font('body-sm-light'))};
  color: ${color('gray4')};
  display: inline-flex;
  outline: none;
  border-radius: 4px;
  border: 1px solid #d4d4d4;
  width: auto;
  height: 40px;
  padding: 0 16px;
  cursor: pointer;
  appearance: none;
  min-width: 97px;
  padding-right: 25px;
  background-repeat: no-repeat;
  background-position: calc(100% - 5px) center;
  background-image: url('data:image/svg+xml,<svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M13.6645 1.08019L6.99762 7.98731L0.332031 1.08015L0.927048 0.487338L6.99771 6.77797L13.0695 0.487305L13.6645 1.08019Z" fill="%23242323"/></svg>');
  &:focus {
    color: ${color('gray4')};
    border: 1px solid ${color('fg')};
  }
  @media screen and (${query.atLeast('desktop')}) {
    ${font('body-md-light')};
  }
`;

export const SelectOption = styled.option``;

export const Fieldset = styled.fieldset``;

export const CheckBox = styled.input.attrs({ type: 'checkbox' })<{
  $icon?: string;
  $iconX?: string;
  $iconY?: string;
}>`
  ${font('body-sm-light')};
  color: ${color('gray2')};
  display: inline-flex;
  outline: none;
  border-radius: 4px;
  border: 1px solid ${color('gray2')};
  width: 14px;
  height: 14px;
  margin-right: 10px;
  cursor: pointer;
  accent-color: ${color('fg')};

  @media screen and (${query.atLeast('desktop')}) {
    ${font('body-md-light')};
  }

  &:hover {
    color: ${color('fg')};
  }
  &:focus {
    color: ${color('fg')};
    border: 1px solid ${color('fg')};
  }
  // icon
  ${({ $icon, $iconX, $iconY }) =>
    $icon &&
    `
    appearance: none;
    &:checked {
      background-image: url(${$icon});
      background-size: 14px;
      background-repeat: no-repeat;
      background-position: ${$iconX || '4px'} ${$iconY || '6px'};
    }
    @media (max-width: ${query.atLeast('tablet')}) {
      width: 40px;
    }
  `}
`;

export const RadioBox = styled.input.attrs({ type: 'radio' })<{
  $icon: string;
  $iconX?: string;
  $iconY?: string;
}>`
  ${font('body-md')};
  color: ${color('fg')};
  display: inline-flex;
  outline: none;
  border-radius: 42px;
  border: 1px solid ${color('fg')};
  width: 25px;
  height: 25px;
  margin-right: 10px;
  cursor: pointer;
  accent-color: ${color('fg')};

  &:hover {
    background-color: ${color('gray5')};
  }

  &:focus {
    color: ${color('fg')};
    border: 1px solid ${color('fg')};
  }

  // icon
  ${({ $icon, $iconX, $iconY }) =>
    $icon &&
    `
    appearance: none;
    &:checked {
      background-image: url(${$icon});
      background-size: 15px;
      background-repeat: no-repeat;
      background-position: ${$iconX || '4px'} ${$iconY || '6px'};
    }

    @media (max-width: ${query.atLeast('tablet')}) {
      width: 40px;
    }
  `}
`;

export const Calendar = styled.input.attrs({ type: 'date' })<{
  $icon: string;
  $iconX?: string;
  $iconY?: string;
}>`
  ${font('body-md')};
  color: ${color('fg')};
  display: inline-flex;
  outline: none;
  border-radius: 2px;
  border: 1px solid ${color('gray4')};
  width: auto;
  height: 28px;
  cursor: pointer;
  accent-color: ${color('gray4')};
  padding: 10px;

  &:focus {
    color: ${color('gray5')};
    border: 1px solid ${color('gray5')};
  }

  // icon
  ${({ $icon, $iconX, $iconY }) =>
    $icon &&
    `
    appearance: none;
    background-image: url(${$icon});
    background-size: 16px;
    background-repeat: no-repeat;
    background-position: ${$iconX || '97%'} ${$iconY || '16px'};

    &::-webkit-inner-spin-button,
    &::-webkit-calendar-picker-indicator {
      opacity: 0;
      cursor: pointer;
    }
  `}
`;

export const InputFieldAdornment = styled.div<{
  $position: string;
  $staticText?: string;
  $disabled: boolean;
}>`
  display: flex;
  border: 1px solid #d4d4d4;
  height: 40px;
  padding: 0 16px;
  border-radius: 4px;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  ${font('body-sm-light')};
  color: ${color('fg')};
  background-color: ${({ $disabled }) => ($disabled ? `${color('gray1')()}` : 'transparent')};
  &:focus-within {
    color: ${color('gray5')};
  }
  ${({ $position, $staticText }) =>
    $staticText &&
    $position === 'start' &&
    `
    text-align: left;
    &:before {
      display: inline-block;
      content: "${$staticText}";
    }
  `}

  ${({ $position, $staticText }) =>
    $staticText &&
    $position === 'end' &&
    `
    text-align: right;
    &:after {
      display: inline-block;
      content: "${$staticText}";
    }
  `}
  input {
    outline: none;
    border: 0;
    padding: 0;
    width: 100%;
    height: 100%;
    text-align: inherit;
    box-sizing: border-box;
    background-color: transparent;
    &:focus {
      border: 0;
    }
  }
`;

export const InputPaymentGroup = styled.div`
  display: flex;
  border: 1px solid #d4d4d4;
  height: 40px;
  padding: 0 16px;
  border-radius: 4px;
  align-items: center;
  ${font('body-sm-light')};
  color: ${color('gray4')};
  width: 100%;
  box-sizing: border-box;
  position: relative;
  &:focus-within {
    border: 1px solid black;
  }
  &:focus-within,
  &:focus-within input {
    color: ${color('gray4')};
  }
  &:before {
    content: ' ';
    height: 16px;
    position: absolute;
    right: 70px;
    width: 1px;
    background-color: #d4d4d4;
  }
  &:after {
    display: inline-block;
    content: '%';
  }
  input {
    outline: none;
    border: 0;
    padding: 0;
    width: 100%;
    height: 100%;
    text-align: inherit;
    box-sizing: border-box;
    &:focus {
      border: 0;
    }
    &:last-child {
      width: 43px;
      padding-left: 16px;
      text-align: right;
    }
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
  position: relative;
  align-items: center;
  width: 100%;
`;

const StyledPhoneInput = styled(PhoneInput)<{
  $isError?: boolean;
}>`
  display: flex;
  flex-direction: row-reverse;
  width: 100%;

  .form-control {
    width: 100%;
    ${font('body-sm')};
    font-style: normal;
    font-weight: 400;
    padding: 14px 14px;
    height: 48px;
    max-height: 48px;
    font-size: 1rem;
    height: 100%;
    color: ${color('gray4')};
    border: 1px solid ${color('gray2')};
    border-radius: 0px 4px 4px 0px;
    outline: none;
    font-style: normal;

    transition: border-color ease;

    &::placeholder {
      color: ${color('gray4')};
    }
    &:focus {
      color: ${color('fg')};
      border: 1px solid ${color('fg')};
    }
    &.warning {
      border: 1px solid ${color('warning')};
    }
  }

  .country-list {
    padding: 0px;

    .search {
      padding: 10px;
      .search-box {
        margin: 0px;
      }
    }
  }

  .flag-dropdown {
    border: none;
    position: relative;
    background: none;
    min-width: 48px;
    height: 48px;
    padding: 0px;
    background-color: ${color('bg')};
    border-radius: 4px 0px 0px 4px;
    border: 1px solid ${color('gray2')};
    border-right: 0px;

    &:focus {
      color: ${color('fg')};
      border: 1px solid ${color('fg')};
    }
    .open {
      border-radius: 4px 0px 0px 4px;
    }
    .selected-flag {
      padding: 0px 10px;
    }
  }
`;

interface PhoneInputFieldProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  isError?: boolean;
  locale?: string;
}

export const PhoneInputField: React.FC<PhoneInputFieldProps> = ({
  value,
  onChange,
  onBlur,
  placeholder = 'Enter phone number',
  disabled = false,
  id,
  isError = false,
  locale = 'en',
}) => {
  return (
    <Wrapper id={id}>
      <StyledPhoneInput
        enableSearch
        disableSearchIcon
        country="us"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        localization={locale === 'es' ? es : locale === 'fr' ? fr : undefined}
        $isError={isError}
        preferredCountries={['us', 'ca']}
        onBlur={onBlur}
        countryCodeEditable={false}
        inputProps={{
          name: 'phone',
          required: true,
        }}
      />
    </Wrapper>
  );
};

export default PhoneInputField;

interface ErrorMessageProps {
  message: string;
  style?: React.CSSProperties;
  dataTestId?: string;
}

//Error message
export const ErrorMessage: FC<ErrorMessageProps> = ({ message, style, dataTestId }) => {
  return (
    <ErrorMessageStyling data-testid={dataTestId} style={style}>
      <Error />
      <MessageWrapper>{message}</MessageWrapper>
    </ErrorMessageStyling>
  );
};
const ErrorMessageStyling = styled.div`
  color: ${color('cta')};
  width: 100%;
  padding-top: ${space('sm')};
  display: inline-flex;
  align-items: center;

  span {
    ${font('body-xs-light')};
    line-height: 1.1;
    padding-top: ${space('xs')};
    padding-bottom: ${space('xs')};

    @media screen and (${query.atLeast('desktop')}) {
      ${font('body-sm-light')};
      line-height: 1.1;
    }
  }

  svg {
    margin-right: ${space('xs')};
  }
`;
const MessageWrapper = styled.span``;
